import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { userProfileService, workoutPlanService, realtimeService } from '../../services/supabaseService';
import ClientOverviewCard from './components/ClientOverviewCard';
import CompletionTrackingPanel from './components/CompletionTrackingPanel';
import AdherenceMetricsChart from './components/AdherenceMetricsChart';
import AddClientModal from './components/AddClientModal';
import GlowRing from './components/GlowRing';
import LiveActivityFeed from './components/LiveActivityFeed';
import OnboardingTour, { TOUR_STORAGE_KEY } from '../../components/ui/OnboardingTour';

// Glassmorphic card style
const glassCard = {
  background: 'rgba(255,255,255,0.05)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 16,
};

const statCardStyle = {
  ...glassCard,
  padding: '20px 24px',
};

const TrainerDashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [assignedWorkouts, setAssignedWorkouts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // Check if tour should be shown (triggered by onboarding completion)
  useEffect(() => {
    const tourDone = localStorage.getItem(TOUR_STORAGE_KEY + 'trainer_dashboard');
    const fromOnboarding = localStorage.getItem('ff_show_tour_trainer');
    if (!tourDone && fromOnboarding === 'true') {
      localStorage.removeItem('ff_show_tour_trainer');
      // Delay to let dashboard fully render
      const t = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (userProfile?.role !== 'personal_trainer') {
      navigate('/dashboard');
      return;
    }
    loadDashboardData();
  }, [user, userProfile]);

  useEffect(() => {
    if (!user?.id || userProfile?.role !== 'personal_trainer') return;

    const unsubscribeWorkouts = realtimeService?.subscribeToAssignedWorkouts(
      user?.id,
      ({ event, data }) => {
        if (event === 'INSERT') {
          setAssignedWorkouts(prev => [...prev, data]);
        } else if (event === 'UPDATE') {
          setAssignedWorkouts(prev => prev?.map(w => w?.id === data?.id ? data : w));
        } else if (event === 'DELETE') {
          setAssignedWorkouts(prev => prev?.filter(w => w?.id !== data?.id));
        }
      }
    );

    const clientIds = clients?.map(c => c?.id) || [];
    const unsubscribePhotos = clientIds?.length > 0
      ? realtimeService?.subscribeToProgressPhotos(clientIds, ({ event, data }) => {
          console.log('New progress photo uploaded:', data);
        })
      : null;

    const unsubscribeNotes = clientIds?.length > 0
      ? realtimeService?.subscribeToProgressNotes(clientIds, ({ event, data }) => {
          console.log('New progress note added:', data);
        })
      : null;

    return () => {
      unsubscribeWorkouts?.();
      unsubscribePhotos?.();
      unsubscribeNotes?.();
    };
  }, [user, userProfile, clients]);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const { data: clientsList } = await userProfileService?.getTrainerClients(user?.id);
      setClients(clientsList || []);
      const { data: workouts } = await workoutPlanService?.getAssignedWorkouts(user?.id, 'personal_trainer');
      setAssignedWorkouts(workouts || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateClientStats = (clientId) => {
    const clientWorkouts = assignedWorkouts?.filter(w => w?.clientId === clientId);
    const totalWorkouts = clientWorkouts?.length;
    const completedWorkouts = clientWorkouts?.filter(w => w?.status === 'completed')?.length;
    const scheduledWorkouts = clientWorkouts?.filter(w => w?.status === 'scheduled')?.length;
    const inProgressWorkouts = clientWorkouts?.filter(w => w?.status === 'in_progress')?.length;
    const completionRate = totalWorkouts > 0 ? Math.round((completedWorkouts / totalWorkouts) * 100) : 0;

    const recentWorkouts = clientWorkouts?.filter(w => {
      const scheduledDate = new Date(w?.scheduledDate);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
      return scheduledDate >= thirtyDaysAgo;
    });
    const recentCompleted = recentWorkouts?.filter(w => w?.status === 'completed')?.length;
    const adherenceScore = recentWorkouts?.length > 0 ? Math.round((recentCompleted / recentWorkouts?.length) * 100) : 0;

    return { totalWorkouts, completedWorkouts, scheduledWorkouts, inProgressWorkouts, completionRate, adherenceScore };
  };

  const needsAttention = (clientId) => {
    const stats = calculateClientStats(clientId);
    return stats?.adherenceScore < 70 || stats?.scheduledWorkouts > 5;
  };

  const filteredClients = clients?.filter(client => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'needs_attention') return needsAttention(client?.id);
    return true;
  });

  const sortedClients = [...filteredClients]?.sort((a, b) => {
    const aNeedsAttention = needsAttention(a?.id);
    const bNeedsAttention = needsAttention(b?.id);
    if (aNeedsAttention && !bNeedsAttention) return -1;
    if (!aNeedsAttention && bNeedsAttention) return 1;
    return 0;
  });

  const totalClients = clients?.length;
  const activeClients = clients?.filter(c => calculateClientStats(c?.id)?.scheduledWorkouts > 0)?.length;
  const clientsNeedingAttention = clients?.filter(c => needsAttention(c?.id))?.length;
  const totalCompletedWorkouts = assignedWorkouts?.filter(w => w?.status === 'completed')?.length;
  const overallCompletionRate = assignedWorkouts?.length > 0
    ? Math.round((totalCompletedWorkouts / assignedWorkouts?.length) * 100)
    : 0;

  // Demo metric values for ring visualizations
  const dailySteps = 8547;
  const caloriesBurned = 1850;
  const waterGlasses = 6.5;

  const handleMessageClient = (clientId) => navigate('/communication-hub', { state: { selectedClientId: clientId } });
  const handleAssignWorkout = (clientId) => navigate('/workout-catalog', { state: { preselectedClientId: clientId } });
  const handleViewProgress = (clientId) => navigate('/progress-reports', { state: { clientId } });

  return (
    <>
      <Helmet>
        <title>Trainer Dashboard - Form Forge</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;600;700;800;900&display=swap" rel="stylesheet" />
      </Helmet>
      {/* Full-page Industrial Pro wrapper */}
      <div style={{ minHeight: '100vh', background: '#0A0A0B', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>
        <Header />

        <main style={{ maxWidth: 1400, margin: '0 auto', padding: '0 24px 48px' }}>

          {/* ── Mesh Gradient Hero / Profile Header ── */}
          <div
            data-tour="trainer-hero"
            style={{
              position: 'relative',
              borderRadius: 20,
              overflow: 'hidden',
              marginBottom: 32,
              marginTop: 24,
              padding: '36px 40px',
              background: 'linear-gradient(135deg, #0d1117 0%, #0f1a2e 25%, #0a1628 50%, #0d1a0d 75%, #0A0A0B 100%)',
            }}>
            {/* Mesh gradient blobs */}
            <div style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              background: 'radial-gradient(ellipse 60% 80% at 80% 50%, rgba(0,212,255,0.08) 0%, transparent 60%), radial-gradient(ellipse 40% 60% at 20% 30%, rgba(170,255,0,0.06) 0%, transparent 60%), radial-gradient(ellipse 50% 70% at 50% 80%, rgba(255,107,0,0.05) 0%, transparent 60%)',
            }} />
            <div style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              border: '1px solid rgba(255,255,255,0.06)', borderRadius: 20,
            }} />

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6" style={{ position: 'relative', zIndex: 1 }}>
              <div>
                <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 6 }}>
                  TRAINER PORTAL
                </p>
                <h1 style={{
                  fontFamily: "'Inter Tight', 'Inter', sans-serif",
                  fontWeight: 900,
                  fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
                  color: '#fff',
                  letterSpacing: '-0.03em',
                  lineHeight: 1.1,
                  marginBottom: 8,
                }}>
                  Good afternoon,{' '}
                  <span style={{ color: '#AAFF00' }}>
                    {userProfile?.fullName?.split(' ')?.[0] || 'Coach'}!
                  </span>
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem' }}>
                  Comprehensive client management and performance monitoring
                </p>
              </div>

              <div className="flex items-center gap-4">
                {/* Profile avatar */}
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Member since</p>
                  <p style={{ fontWeight: 700, color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>Jan 2024</p>
                </div>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #AAFF00 0%, #00D4FF 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 0 24px rgba(170,255,0,0.3)',
                  flexShrink: 0,
                }}>
                  <span style={{ fontWeight: 900, fontSize: '1.4rem', color: '#0A0A0B' }}>
                    {userProfile?.fullName?.charAt(0)?.toUpperCase() || 'T'}
                  </span>
                </div>
                <Button
                  data-tour="trainer-add-client"
                  variant="default"
                  onClick={() => setIsAddClientOpen(true)}
                  style={{ background: '#AAFF00', color: '#0A0A0B', fontWeight: 700, border: 'none' }}
                >
                  <Icon name="UserPlus" size={16} className="mr-2" />
                  Add Client
                </Button>
              </div>
            </div>
          </div>

          {/* ── Neon Ring Metrics Row ── */}
          <div data-tour="trainer-metrics" className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Daily Steps */}
            <div style={{ ...glassCard, padding: '28px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600 }}>
                Daily Steps
              </p>
              <GlowRing value={dailySteps} max={10000} color="lime" sublabel="steps" label />
            </div>

            {/* Calories Burned */}
            <div style={{ ...glassCard, padding: '28px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600 }}>
                Calories Burned
              </p>
              <GlowRing value={caloriesBurned} max={2200} color="orange" sublabel="kcal" label />
            </div>

            {/* Water Intake */}
            <div style={{ ...glassCard, padding: '28px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600 }}>
                Water Intake
              </p>
              <GlowRing value={waterGlasses} max={8} color="cyan" sublabel="glasses" label />
            </div>
          </div>

          {/* ── Overview Stat Cards ── */}
          <div data-tour="trainer-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Clients', value: totalClients, icon: 'Users', accent: '#AAFF00' },
              { label: 'Active Clients', value: activeClients, icon: 'Activity', accent: '#22c55e' },
              { label: 'Needs Attention', value: clientsNeedingAttention, icon: 'AlertCircle', accent: '#FF6B00' },
              { label: 'Completion Rate', value: `${overallCompletionRate}%`, icon: 'CheckCircle', accent: '#00D4FF' },
            ]?.map((stat) => (
              <div key={stat?.label} style={statCardStyle}>
                <div className="flex items-center gap-3">
                  <div style={{
                    width: 44, height: 44, borderRadius: 10,
                    background: `${stat?.accent}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon name={stat?.icon} size={22} style={{ color: stat?.accent }} />
                  </div>
                  <div>
                    <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      {stat?.label}
                    </p>
                    <p style={{
                      fontFamily: "'Inter Tight', 'Inter', sans-serif",
                      fontWeight: 800,
                      fontSize: '1.6rem',
                      color: stat?.accent,
                      lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                      textShadow: `0 0 16px ${stat?.accent}55`,
                    }}>
                      {stat?.value}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Main Content + Live Feed ── */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 mb-8">
            {/* Completion Tracking — spans 3 cols */}
            <div data-tour="trainer-completion" className="xl:col-span-3">
              <div style={{ ...glassCard, overflow: 'hidden' }}>
                <CompletionTrackingPanel
                  assignedWorkouts={assignedWorkouts}
                  clients={clients}
                />
              </div>
            </div>

            {/* Live Activity Feed — 1 col */}
            <div data-tour="trainer-live-feed" className="xl:col-span-1">
              <LiveActivityFeed clients={clients} assignedWorkouts={assignedWorkouts} />
            </div>
          </div>

          {/* ── Adherence Metrics Chart ── */}
          <div data-tour="trainer-adherence" style={{ ...glassCard, overflow: 'hidden', marginBottom: 32 }}>
            <AdherenceMetricsChart
              clients={clients}
              calculateClientStats={calculateClientStats}
            />
          </div>

          {/* ── Client Management Section ── */}
          <div data-tour="trainer-clients" style={{ ...glassCard, padding: '28px' }}>
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
              <div>
                <h2 style={{
                  fontFamily: "'Inter Tight', 'Inter', sans-serif",
                  fontWeight: 800,
                  fontSize: '1.2rem',
                  color: '#fff',
                  letterSpacing: '-0.01em',
                }}>
                  Client Overview
                </h2>
                <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                  Manage your clients and track their progress
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant={filterStatus === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('all')}
                >
                  All ({clients?.length})
                </Button>
                <Button
                  variant={filterStatus === 'needs_attention' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('needs_attention')}
                >
                  <Icon name="AlertCircle" size={16} className="mr-1" />
                  Needs Attention ({clientsNeedingAttention})
                </Button>
                <div className="border-l border-border mx-2" />
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                >
                  <Icon name="Grid" size={16} />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <Icon name="List" size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddClientOpen(true)}
                >
                  <Icon name="UserPlus" size={16} className="mr-1" />
                  Add Client
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  border: '3px solid rgba(170,255,0,0.15)',
                  borderTopColor: '#AAFF00',
                  animation: 'spin 0.8s linear infinite',
                }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : (
              <>
                {sortedClients?.length > 0 ? (
                  <div className={viewMode === 'cards' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
                    {sortedClients?.map((client) => {
                      const stats = calculateClientStats(client?.id);
                      const needsAttentionFlag = needsAttention(client?.id);
                      return (
                        <ClientOverviewCard
                          key={client?.id}
                          client={client}
                          stats={stats}
                          needsAttention={needsAttentionFlag}
                          viewMode={viewMode}
                          onMessage={() => handleMessageClient(client?.id)}
                          onAssignWorkout={() => handleAssignWorkout(client?.id)}
                          onViewProgress={() => handleViewProgress(client?.id)}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <Icon name="Users" size={48} style={{ color: 'rgba(255,255,255,0.2)', margin: '0 auto 16px' }} />
                    <p style={{ color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>
                      {filterStatus === 'needs_attention' ? 'No clients need attention right now' : 'No clients assigned yet'}
                    </p>
                    {filterStatus === 'all' && (
                      <Button variant="default" onClick={() => setIsAddClientOpen(true)}>
                        <Icon name="UserPlus" size={16} className="mr-2" />
                        Add Your First Client
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      <AddClientModal
        isOpen={isAddClientOpen}
        onClose={() => setIsAddClientOpen(false)}
        trainerId={user?.id}
        onClientAdded={(clientId) => {
          loadDashboardData();
          if (clientId) navigate(`/client-assessment?clientId=${clientId}`);
        }}
      />

      {/* Onboarding Tour */}
      {showTour && (
        <OnboardingTour
          tourKey="trainer_dashboard"
          steps={[
            {
              target: '[data-tour="trainer-hero"]',
              title: 'Your Trainer Portal',
              description: 'This is your coaching command centre. See your name, role, and quick access to key actions at a glance.',
              icon: 'LayoutDashboard',
              position: 'bottom',
            },
            {
              target: '[data-tour="trainer-add-client"]',
              title: 'Add a Client',
              description: 'Invite new clients to your roster here. Once added, you can assign workouts, track progress, and message them directly.',
              icon: 'UserPlus',
              position: 'bottom',
            },
            {
              target: '[data-tour="trainer-metrics"]',
              title: 'Your Activity Rings',
              description: 'Track your own daily steps, calories, and water intake with neon glow rings — stay on top of your personal health too.',
              icon: 'Activity',
              position: 'bottom',
            },
            {
              target: '[data-tour="trainer-stats"]',
              title: 'Client Overview Stats',
              description: 'A quick snapshot of your total clients, active clients, who needs attention, and your overall programme completion rate.',
              icon: 'BarChart2',
              position: 'bottom',
            },
            {
              target: '[data-tour="trainer-completion"]',
              title: 'Completion Tracking',
              description: 'Monitor workout completion across all clients. Spot trends and identify who may need a check-in or programme adjustment.',
              icon: 'CheckCircle',
              position: 'top',
            },
            {
              target: '[data-tour="trainer-live-feed"]',
              title: 'Live Activity Feed',
              description: 'Real-time updates as clients complete workouts, log meals, or hit milestones — stay connected without constant check-ins.',
              icon: 'Zap',
              position: 'left',
            },
            {
              target: '[data-tour="trainer-adherence"]',
              title: 'Adherence Metrics',
              description: 'Visualise how consistently each client follows their programme. Use this to have informed, data-driven coaching conversations.',
              icon: 'TrendingUp',
              position: 'top',
            },
            {
              target: '[data-tour="trainer-clients"]',
              title: 'Client Management',
              description: 'Your full client roster. Message, assign workouts, or view detailed progress reports — all from this section.',
              icon: 'Users',
              position: 'top',
            },
          ]}
          onComplete={() => setShowTour(false)}
          onSkip={() => setShowTour(false)}
        />
      )}
    </>
  );
};

export default TrainerDashboard;